import { FC, useState, useEffect } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { motion } from 'framer-motion';

import FormHeader from '../../components/FormHeader/FormHeader';
import Input from '../../components/Input/Input';
import InputSelectDropdown from '../../components/InputSelectDropdown/InputSelectDropdown';
import RichText from '../../components/RichText/RichText';
import Switch from 'components/Switch/Switch';

import {
  BillingCyclesUnit,
  BillingPeriodUnit,
  CreatePlan,
  TrialPeriodUnit
} from '../../common/contracts';

import usePlans from '../../api/usePlans';
import {
  BILLING_CYCLES_UNIT_OPTIONS,
  BILLING_PERIOD_UNIT_OPTIONS
} from './constants';
import { getPlanSchema } from './utils';

type SubscriptionsFormProps = {
  edit?: boolean;
};

const SubscriptionsForm: FC<SubscriptionsFormProps> = ({ edit = false }) => {
  const navigate = useNavigate();

  const { planId } = useParams();
  const { createPlan, getPlan } = usePlans();

  const [isFirstPaymentDiscountEnabled, setIsFirstPaymentDiscountEnabled] =
    useState<boolean>(false);
  const [billingCycleUnit, setBillingCycleUnit] = useState<BillingCyclesUnit>(
    BillingCyclesUnit.FIXED
  );
  const [billingPeriodUnit, setBillingPeriodUnit] = useState<BillingPeriodUnit>(
    BillingPeriodUnit.DAILY
  );
  const [trialPeriodUnit, setTrialPeriodUnit] = useState<TrialPeriodUnit>(
    TrialPeriodUnit.MONTHS
  );

  const isFixedBillingCycle = billingCycleUnit === BillingCyclesUnit.FIXED;

  const {
    handleChange,
    handleBlur,
    values,
    touched,
    errors,
    submitForm,
    setValues,
    isValid
  } = useFormik({
    validateOnMount: true,
    validationSchema: getPlanSchema(isFixedBillingCycle),
    enableReinitialize: true,
    initialValues: {
      name: '',
      displayName: '',
      description: '',
      billingFrequency: '',
      price: 0,
      trialPeriod: 0,
      trialPeriodUnit: '',
      billingCycle: BillingCyclesUnit.FIXED,
      billingCycleValue: 0,
      firstPaymentDiscount: 0
    },
    onSubmit: async (values) => {
      console.log('values', values);
      const planToSubmit: CreatePlan = {
        name: values.name,
        displayName: values.displayName,
        description: values.description,
        billingPeriodUnit: billingPeriodUnit,
        billingCycles: values.billingCycleValue,
        basePriceInUsd: values.price,
        trialPeriod: values.trialPeriod,
        trialPeriodUnit: trialPeriodUnit,
        billingCyclesUnit: billingCycleUnit
      };

      await createPlan.mutateAsync(planToSubmit, {
        onSuccess: (...params) => {
          console.log('success creating plan', params);
          navigate('../');
        },
        onError: (error) => {
          console.log('error creating plan', error);
        }
      });
    }
  });

  useEffect(() => {
    if (edit && getPlan.data) {
      setValues({
        planName: getPlan.data.externalName,
        description: getPlan.data.description || '',
        billingFrequency: getPlan.data.billingPeriodUnit,
        price: getPlan.data.basePriceInUsd,
        trialPeriod: getPlan.data.trialPeriod,
        trialPeriodUnit: getPlan.data.trialPeriodUnit,
        billingCycle: getPlan.data.billingCyclesUnit,
        billingCycleValue: getPlan.data.billingCycles,
        externalName: getPlan.data.externalName
        // firstPaymentDiscount: getPlan.data.firstPaymentDiscount
      } as any);
      // setBillingCycleUnit(getPlan.data.billingCyclesUnit);
      // setBillingPeriodUnit(getPlan.data.billingPeriodUnit);
      // setTrialPeriodUnit(getPlan.data.trialPeriodUnit);
    }
  }, [getPlan.data, edit, setValues]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className="bg-white fixed h-screen w-full top-0 left-0 z-[1000] overflow-hidden"
    >
      <FormHeader
        onCloseForm={() => navigate('../')}
        closeFormOnSubmit={false}
        onSubmitForm={() => submitForm()}
        title={`${edit ? 'Edit plan' : 'Create new plan'} `}
        isFormValid={isValid}
        buttonText="Save"
      />

      <div className="mt-7 w-full mx-auto overflow-y-auto h-[calc(100vh-160px)]">
        <div className="w-[456px] mx-auto">
          <RichText
            title={edit ? 'Edit plan' : 'Create new plan'}
            description={
              edit
                ? `Plan ID Number: ${planId}`
                : 'A subscription plan enables recurring access for your players and can be configured to suit your monetization strategy.'
            }
            className="mb-9"
          />
          <div className="flex flex-col gap-5 mb-4">
            <div className="grid grid-cols-1 gap-5">
              <Input
                disabled={edit}
                label="Plan Name"
                placeholder="name"
                type="text"
                name="name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                isRequired
                state={touched.name && errors.name ? 'error' : 'default'}
                description={touched.name && errors.name ? errors.name : ''}
              />

              <Input
                disabled={edit}
                label="Plan display name"
                placeholder="Display name"
                type="text"
                name="displayName"
                value={values.displayName}
                onChange={handleChange}
                onBlur={handleBlur}
                isRequired
                state={
                  touched.displayName && errors.displayName
                    ? 'error'
                    : 'default'
                }
                description={
                  touched.displayName && errors.displayName
                    ? errors.displayName
                    : ''
                }
              />

              <Input
                label="Description"
                placeholder="Description"
                type="text"
                name="description"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                state={
                  touched.description && errors.description
                    ? 'error'
                    : 'default'
                }
                description={
                  touched.description && errors.description
                    ? errors.description
                    : ''
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-5">
              <InputSelectDropdown
                isRequired
                label="Billing frequency"
                options={BILLING_PERIOD_UNIT_OPTIONS}
                onOpenChange={(newValue: any) => {
                  setBillingPeriodUnit(newValue);
                }}
              />

              <Input
                label="Price"
                placeholder="29.99"
                type="number"
                name="price"
                value={values.price}
                onChange={handleChange}
                isRequired
                state={touched.price && errors.price ? 'error' : 'default'}
                description={touched.price && errors.price ? errors.price : ''}
                leftIcon={<span>$</span>}
                leftIconStyles="text-base-foreground text-p-s absolute left-3 top-1/2 -translate-y-1/2"
                min={0.8}
                step={0.1}
              />
            </div>
          </div>

          <div className="flex flex-col gap-5 mb-9 w-full">
            <div className="grid grid-cols-2 gap-5">
              {/* <Input
                label="Free Trial Period"
                type="number"
                name="trialPeriod"
                value={values.trialPeriod}
                onChange={handleChange}
                onKeyDown={(e: any) => {
                  if (e.key === '-' || e.key === 'Minus') {
                    e.preventDefault();
                  }
                }}
                min={0}
              />

              <InputSelectDropdown
                label="&nbsp;"
                options={TRIAL_PERIOD_UNIT_OPTIONS}
                placeholder={'Days'}
                onOptionChange={(newValue: any) => setTrialPeriodUnit(newValue)}
              /> */}
            </div>

            <div className="grid grid-cols-2 gap-5">
              <InputSelectDropdown
                label="Billing Cycles"
                options={BILLING_CYCLES_UNIT_OPTIONS}
                placeholder={'Fixed'}
                onOptionChange={(newValue: any) =>
                  setBillingCycleUnit(newValue)
                }
              />

              <Input
                label="&nbsp;"
                type="number"
                name="billingCycleValue"
                disabled={billingCycleUnit === BillingCyclesUnit.FOREVER}
                value={values.billingCycleValue}
                onChange={handleChange}
                onKeyDown={(e) => {
                  if (e.key === '-' || e.key === 'Minus') {
                    e.preventDefault();
                  }
                }}
                min={1}
              />
            </div>
          </div>

          <Switch
            label="First Payment discount"
            description="Allows the publisher to offer a first time discount to a plan"
            defaultChecked={isFirstPaymentDiscountEnabled}
            textAlignment="right"
            size="lg"
            onCheckedChange={() => {
              setIsFirstPaymentDiscountEnabled(!isFirstPaymentDiscountEnabled);
            }}
          />

          <Input
            label=""
            placeholder="29.99"
            type="number"
            name="firstPaymentDiscount"
            value={values.firstPaymentDiscount}
            disabled={!isFirstPaymentDiscountEnabled}
            onChange={handleChange}
            isRequired
            state={touched.price && errors.price ? 'error' : 'default'}
            description={touched.price && errors.price ? errors.price : ''}
            leftIcon={<span>%</span>}
            leftIconStyles="text-base-foreground text-p-s absolute left-3 top-1/2 -translate-y-1/2"
            min={1}
            max={90}
            step={0.1}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default SubscriptionsForm;
